// Unified Notification API - supports both hook-based (for components) and promise-based (for Redux) usage
import { useCallback } from 'react';
import useHttp from '../../hooks/useHttp';

// Base API configuration
const API_BASE_URL = `${process.env.REACT_APP_IBILL_GATEWAY}/notification/api/v1` || 'http://localhost:8080/api/v1';

// Utility functions for promise-based API
const getAuthHeaders = () => {
  const token = localStorage.getItem('ibill_access_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` })
  };
};

const handleResponse = async (response) => {
  const jsonResp = await response.json();

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${jsonResp.message || 'Request failed'}`);
  }

  // Handle both direct responses and ApiResponse wrapper format
  if (jsonResp.status === 'OK' || jsonResp.status === 'NO_CONTENT' || jsonResp.status === 'CREATED' ||
      (jsonResp.status && jsonResp.status.code === 200)) {
    return jsonResp.data || jsonResp;
  }

  if (response.ok && !jsonResp.status) {
    return jsonResp;
  }

  // Don't throw error for successful HTTP responses
  if (response.ok) {
    return jsonResp;
  }

  throw new Error(jsonResp.message || 'Request failed');
};

// ========================================
// HOOK-BASED API (for React Components)
// ========================================
const useNotificationApi = () => {
  const { isLoading, error, sendRequest } = useHttp();

  // Template Management APIs
  const getTemplates = useCallback((companyId, page = 0, size = 20, search, channel, callback) => {
    const params = new URLSearchParams({
      companyId: companyId.toString(),
      page: page.toString(),
      size: size.toString(),
    });

    if (search) params.append('search', search);
    if (channel) params.append('channel', channel);

    sendRequest({
      url: `${API_BASE_URL}/admin/templates?${params}`,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    }, callback);
  }, [sendRequest]);

  const getTemplate = useCallback((id, callback) => {
    sendRequest({
      url: `${API_BASE_URL}/admin/templates/${id}`,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    }, callback);
  }, [sendRequest]);

  const createTemplate = useCallback((templateData, callback) => {
    sendRequest({
      url: `${API_BASE_URL}/admin/templates`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: templateData
    }, callback);
  }, [sendRequest]);

  const updateTemplate = useCallback((id, templateData, callback) => {
    sendRequest({
      url: `${API_BASE_URL}/admin/templates/${id}`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: templateData
    }, callback);
  }, [sendRequest]);

  const deleteTemplate = useCallback((id, callback) => {
    sendRequest({
      url: `${API_BASE_URL}/admin/templates/${id}`,
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' }
    }, callback);
  }, [sendRequest]);

  const toggleTemplateStatus = useCallback((id, callback) => {
    sendRequest({
      url: `${API_BASE_URL}/admin/templates/${id}/toggle-status`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' }
    }, callback);
  }, [sendRequest]);

  const validateTemplate = useCallback((templateData, callback) => {
    sendRequest({
      url: `${API_BASE_URL}/admin/templates/validate`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: templateData
    }, callback);
  }, [sendRequest]);

  // Template Variables APIs
  const getVariablesByTemplate = useCallback((templateId, callback) => {
    sendRequest({
      url: `${API_BASE_URL}/admin/template-variables/template/${templateId}`,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    }, callback);
  }, [sendRequest]);

  const createVariable = useCallback((variableData, callback) => {
    sendRequest({
      url: `${API_BASE_URL}/admin/template-variables`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: variableData
    }, callback);
  }, [sendRequest]);

  const bulkUpdateVariables = useCallback((templateId, variables, callback) => {
    sendRequest({
      url: `${API_BASE_URL}/admin/template-variables/template/${templateId}/bulk`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: variables
    }, callback);
  }, [sendRequest]);

  // Email Configuration APIs
  const getEmailConfigs = useCallback((companyId, page = 0, size = 20, applicationName, callback) => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    if (applicationName) params.append('applicationName', applicationName);

    const token = localStorage.getItem('ibill_access_token');
    const headers = {
      'Content-Type': 'application/json',
      'company-id': companyId.toString()
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    sendRequest({
      url: `${API_BASE_URL}/admin/email-configs?${params}`,
      method: 'GET',
      headers
    }, callback);
  }, [sendRequest]);

  const createEmailConfig = useCallback((configData, callback) => {
    const token = localStorage.getItem('ibill_access_token');
    const headers = {
      'Content-Type': 'application/json',
      'company-id': configData.companyId?.toString() || '1'
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const { companyId, ...cleanConfigData } = configData;

    sendRequest({
      url: `${API_BASE_URL}/admin/email-configs`,
      method: 'POST',
      headers,
      body: cleanConfigData
    }, callback);
  }, [sendRequest]);

  const updateEmailConfig = useCallback((id, configData, callback) => {
    const token = localStorage.getItem('ibill_access_token');
    const headers = {
      'Content-Type': 'application/json'
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const { companyId, ...cleanConfigData } = configData;

    sendRequest({
      url: `${API_BASE_URL}/admin/email-configs/${id}`,
      method: 'PUT',
      headers,
      body: cleanConfigData
    }, callback);
  }, [sendRequest]);

  const deleteEmailConfig = useCallback((id, callback) => {
    sendRequest({
      url: `${API_BASE_URL}/admin/email-configs/${id}`,
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' }
    }, callback);
  }, [sendRequest]);

  const testEmailConfig = useCallback((id, testEmail, callback) => {
    sendRequest({
      url: `${API_BASE_URL}/admin/email-configs/${id}/test?testEmailAddress=${testEmail}`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    }, callback);
  }, [sendRequest]);

  const toggleEmailConfigStatus = useCallback((id, callback) => {
    sendRequest({
      url: `${API_BASE_URL}/admin/email-configs/${id}/toggle-status`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' }
    }, callback);
  }, [sendRequest]);

  // SMS Configuration APIs
  const getSmsConfigs = useCallback((companyId, page = 0, size = 20, applicationName, gateway, callback) => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    if (applicationName) params.append('applicationName', applicationName);
    if (gateway) params.append('gateway', gateway);

    const token = localStorage.getItem('ibill_access_token');
    const headers = {
      'Content-Type': 'application/json',
      'company-id': companyId.toString()
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    sendRequest({
      url: `${API_BASE_URL}/admin/sms-configs?${params}`,
      method: 'GET',
      headers
    }, callback);
  }, [sendRequest]);

  const createSmsConfig = useCallback(async (configData, callback) => {
    try {
      const token = localStorage.getItem('ibill_access_token');
      const headers = {
        'Content-Type': 'application/json',
        'company-id': configData.companyId?.toString() || '1'
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const { companyId, ...cleanConfigData } = configData;

      const response = await fetch(`${API_BASE_URL}/admin/sms-configs`, {
        method: 'POST',
        headers,
        body: JSON.stringify(cleanConfigData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (callback) callback(result);
      return result;
    } catch (err) {
      console.error('Error creating SMS config:', err);
      if (callback) callback(null, err);
      throw err;
    }
  }, []);

  const updateSmsConfig = useCallback((id, configData, callback) => {
    const token = localStorage.getItem('ibill_access_token');
    const headers = {
      'Content-Type': 'application/json'
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const { companyId, ...cleanConfigData } = configData;

    sendRequest({
      url: `${API_BASE_URL}/admin/sms-configs/${id}`,
      method: 'PUT',
      headers,
      body: cleanConfigData
    }, callback);
  }, [sendRequest]);

  const deleteSmsConfig = useCallback(async (id, callback) => {
    try {
      const token = localStorage.getItem('ibill_access_token');
      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/admin/sms-configs/${id}`, {
        method: 'DELETE',
        headers
      });

      console.log('Delete SMS config response status:', response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Delete SMS config result:', result);
      callback(result);
    } catch (err) {
      console.error('Error deleting SMS config:', err);
      callback(null, err);
    }
  }, []);

  const testSmsConfig = useCallback((id, testPhoneNumber, callback) => {
    sendRequest({
      url: `${API_BASE_URL}/admin/sms-configs/${id}/test?testPhoneNumber=${testPhoneNumber}`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    }, callback);
  }, [sendRequest]);

  const toggleSmsConfigStatus = useCallback((id, callback) => {
    sendRequest({
      url: `${API_BASE_URL}/admin/sms-configs/${id}/toggle-status`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' }
    }, callback);
  }, [sendRequest]);

  // Notification Service APIs
  const sendNotification = useCallback((notificationData, callback) => {
    sendRequest({
      url: `${API_BASE_URL}/notifications/send`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: notificationData
    }, callback);
  }, [sendRequest]);

  // Configuration Summary API
  const getConfigurationSummary = useCallback((companyId, callback) => {
    sendRequest({
      url: `${API_BASE_URL}/admin/notification-config/company/${companyId}/summary`,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    }, callback);
  }, [sendRequest]);

  const getAvailableGateways = useCallback((callback) => {
    sendRequest({
      url: `${API_BASE_URL}/admin/sms-configs/gateways`,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    }, callback);
  }, [sendRequest]);

  // Template Routes APIs
  const getRoutesByTemplate = useCallback((templateId, channel, callback) => {
    const params = new URLSearchParams();
    if (channel) params.append('channel', channel);

    sendRequest({
      url: `${API_BASE_URL}/admin/template-routes/template/${templateId}?${params}`,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    }, callback);
  }, [sendRequest]);

  const getAvailableRoutes = useCallback((companyId, channel, callback) => {
    const token = localStorage.getItem('ibill_access_token');
    const headers = {
      'Content-Type': 'application/json',
      'company-id': companyId.toString()
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    sendRequest({
      url: `${API_BASE_URL}/admin/template-routes/available?channel=${channel}`,
      method: 'GET',
      headers
    }, callback);
  }, [sendRequest]);

  const createRoute = useCallback((routeData, callback) => {
    sendRequest({
      url: `${API_BASE_URL}/admin/template-routes`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: routeData
    }, callback);
  }, [sendRequest]);

  const updateRoute = useCallback((id, routeData, callback) => {
    sendRequest({
      url: `${API_BASE_URL}/admin/template-routes/${id}`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: routeData
    }, callback);
  }, [sendRequest]);

  const deleteRoute = useCallback((id, callback) => {
    sendRequest({
      url: `${API_BASE_URL}/admin/template-routes/${id}`,
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' }
    }, callback);
  }, [sendRequest]);

  const toggleRouteStatus = useCallback((id, callback) => {
    sendRequest({
      url: `${API_BASE_URL}/admin/template-routes/${id}/toggle-status`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' }
    }, callback);
  }, [sendRequest]);

  return {
    isLoading,
    error,
    // Template APIs
    getTemplates,
    getTemplate,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    toggleTemplateStatus,
    validateTemplate,
    // Template Variables APIs
    getVariablesByTemplate,
    createVariable,
    bulkUpdateVariables,
    // Email Config APIs
    getEmailConfigs,
    createEmailConfig,
    updateEmailConfig,
    deleteEmailConfig,
    testEmailConfig,
    toggleEmailConfigStatus,
    // SMS Config APIs
    getSmsConfigs,
    createSmsConfig,
    updateSmsConfig,
    deleteSmsConfig,
    testSmsConfig,
    toggleSmsConfigStatus,
    // Notification Service APIs
    sendNotification,
    // Configuration APIs
    getConfigurationSummary,
    getAvailableGateways,
    // Template Routes APIs
    getRoutesByTemplate,
    getAvailableRoutes,
    createRoute,
    updateRoute,
    deleteRoute,
    toggleRouteStatus,
  };
};

// ========================================
// PROMISE-BASED API (for Redux Actions)
// ========================================

// Template Management APIs
export const getTemplates = async (companyId, page = 0, size = 20, search, channel) => {
  const params = new URLSearchParams({
    companyId: companyId.toString(),
    page: page.toString(),
    size: size.toString(),
  });

  if (search) params.append('search', search);
  if (channel) params.append('channel', channel);

  const response = await fetch(`${API_BASE_URL}/admin/templates?${params}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

export const createTemplate = async (templateData) => {
  const response = await fetch(`${API_BASE_URL}/admin/templates`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(templateData)
  });

  return handleResponse(response);
};

export const updateTemplate = async (id, templateData) => {
  const response = await fetch(`${API_BASE_URL}/admin/templates/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(templateData)
  });

  return handleResponse(response);
};

export const deleteTemplate = async (id) => {
  const response = await fetch(`${API_BASE_URL}/admin/templates/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

export const toggleTemplateStatus = async (id) => {
  const response = await fetch(`${API_BASE_URL}/admin/templates/${id}/toggle-status`, {
    method: 'PATCH',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

// Configuration Summary API
export const getConfigurationSummary = async (companyId) => {
  const response = await fetch(`${API_BASE_URL}/admin/notification-config/company/${companyId}/summary`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

// Email Configuration APIs
export const getEmailConfigs = async (page = 0, size = 20, applicationName) => {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
  });

  if (applicationName) params.append('applicationName', applicationName);

  const response = await fetch(`${API_BASE_URL}/admin/email-configs?${params}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

export const createEmailConfig = async (configData) => {
  const { companyId, ...payload } = configData;

  const response = await fetch(`${API_BASE_URL}/admin/email-configs`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload)
  });

  return handleResponse(response);
};

export const updateEmailConfig = async (id, configData) => {
  const { companyId, ...payload } = configData;

  const response = await fetch(`${API_BASE_URL}/admin/email-configs/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload)
  });

  return handleResponse(response);
};

export const deleteEmailConfig = async (id) => {
  const response = await fetch(`${API_BASE_URL}/admin/email-configs/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

export const toggleEmailConfigStatus = async (id) => {
  const response = await fetch(`${API_BASE_URL}/admin/email-configs/${id}/toggle-status`, {
    method: 'PATCH',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

export const testEmailConfig = async (id, testEmail) => {
  console.log('API testEmailConfig called with:', { id, testEmail });
  console.log('Full API URL:', `${API_BASE_URL}/admin/email-configs/${id}/test?testEmailAddress=${testEmail}`);
  console.log('Auth headers:', getAuthHeaders());

  try {
    const response = await fetch(`${API_BASE_URL}/admin/email-configs/${id}/test?testEmailAddress=${testEmail}`, {
      method: 'POST',
      headers: getAuthHeaders()
    });

    console.log('Fetch response status:', response.status);
    console.log('Fetch response ok:', response.ok);
    console.log('Fetch response headers:', Object.fromEntries(response.headers.entries()));

    const result = await handleResponse(response);
    console.log('API testEmailConfig result:', result);
    return result;
  } catch (error) {
    console.error('API testEmailConfig error:', error);
    throw error;
  }
};

// SMS Configuration APIs
export const getSmsConfigs = async (page = 0, size = 20, applicationName, gateway) => {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
  });

  if (applicationName) params.append('applicationName', applicationName);
  if (gateway) params.append('gateway', gateway);

  const response = await fetch(`${API_BASE_URL}/admin/sms-configs?${params}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

export const createSmsConfig = async (configData) => {
  const { companyId, ...payload } = configData;

  const response = await fetch(`${API_BASE_URL}/admin/sms-configs`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload)
  });

  return handleResponse(response);
};

export const updateSmsConfig = async (id, configData) => {
  const { companyId, ...payload } = configData;

  const response = await fetch(`${API_BASE_URL}/admin/sms-configs/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload)
  });

  return handleResponse(response);
};

export const deleteSmsConfig = async (id) => {
  const response = await fetch(`${API_BASE_URL}/admin/sms-configs/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

export const toggleSmsConfigStatus = async (id) => {
  const response = await fetch(`${API_BASE_URL}/admin/sms-configs/${id}/toggle-status`, {
    method: 'PATCH',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

export const testSmsConfig = async (id, testPhone) => {
  const response = await fetch(`${API_BASE_URL}/admin/sms-configs/${id}/test?testPhoneNumber=${testPhone}`, {
    method: 'POST',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

// Send Notification API
export const sendNotification = async (notificationData) => {
  const response = await fetch(`${API_BASE_URL}/notifications/send`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(notificationData)
  });

  return handleResponse(response);
};

// Template Route Management APIs
export const getRoutesByTemplate = async (templateId, channel) => {
  const params = new URLSearchParams();
  if (channel) params.append('channel', channel);

  const response = await fetch(`${API_BASE_URL}/admin/template-routes/template/${templateId}?${params}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

export const getAvailableRoutes = async (companyId, channel) => {
  const headers = {
    ...getAuthHeaders(),
    'company-id': companyId.toString()
  };

  const response = await fetch(`${API_BASE_URL}/admin/template-routes/available?channel=${channel}`, {
    method: 'GET',
    headers
  });

  return handleResponse(response);
};

export const createRoute = async (routeData) => {
  const response = await fetch(`${API_BASE_URL}/admin/template-routes`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(routeData)
  });

  return handleResponse(response);
};

export const updateRoute = async (id, routeData) => {
  const response = await fetch(`${API_BASE_URL}/admin/template-routes/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(routeData)
  });

  return handleResponse(response);
};

export const deleteRoute = async (id) => {
  const response = await fetch(`${API_BASE_URL}/admin/template-routes/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

export const toggleRouteStatus = async (id) => {
  const response = await fetch(`${API_BASE_URL}/admin/template-routes/${id}/toggle-status`, {
    method: 'PATCH',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

// Template Variables APIs
export const getVariablesByTemplate = async (templateId) => {
  const response = await fetch(`${API_BASE_URL}/admin/template-variables/template/${templateId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

// Helper functions for configurations
export const getEmailConfigsByCompany = async (companyId) => {
  const headers = {
    ...getAuthHeaders(),
    'company-id': companyId.toString()
  };

  const response = await fetch(`${API_BASE_URL}/admin/email-configs`, {
    method: 'GET',
    headers
  });

  return handleResponse(response);
};

export const getSmsConfigsByCompany = async (companyId) => {
  const headers = {
    ...getAuthHeaders(),
    'company-id': companyId.toString()
  };

  const response = await fetch(`${API_BASE_URL}/admin/sms-configs`, {
    method: 'GET',
    headers
  });

  return handleResponse(response);
};

// Template Routes helper functions
export const getTemplateRoutes = async (templateId) => {
  const response = await fetch(`${API_BASE_URL}/admin/template-routes/template/${templateId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

export const updateTemplateRoutes = async (templateId, routes) => {
  const results = await Promise.all(
    routes.map(async (route) => {
      if (route.id) {
        return updateRoute(route.id, route);
      }
      const routeData = {
        ...route,
        templateId
      };
      return createRoute(routeData);
    })
  );
  return results;
};

// Default export for hook-based usage
export default useNotificationApi;
